// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import OrderDetailsShipmentInfoDetails from "@eisinc/App/Components/OrderDetails/OrderDetailsShipmentInfoDetails";
import { OrderDetailsTableStyle } from "@eisinc/App/SharedStyles/OrderDetailsStyles";
import DataTable, { DataTableProps } from "@eisinc/MobiusOverrides/DataTable/DataTable";
import DataTableBody, { DataTableBodyProps } from "@eisinc/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@eisinc/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@eisinc/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@eisinc/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@eisinc/MobiusOverrides/DataTable/DataTableRow";
import { orderLines, shipmentPackages, shipmentsTrackingData } from "@eisinc/Overrides/Pages/OrderDetailsPage";
import ChevronDown from "@eisinc/XDL/Icons/ChevronDown";
import ChevronUp from "@eisinc/XDL/Icons/ChevronUp";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel, ShipmentPackageDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";

import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext, useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    hideInvoiceInShipments:
        (getSettingsCollection(state).customSettings as any)?.hideInvoiceInShipments?.toLowerCase() === "true",
});

type Props = ReturnType<typeof mapStateToProps>;

export interface OrderDetailsShipmentPackagesStyles {
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    shipmentDateHeader?: DataTableHeaderProps;
    carrierHeader?: DataTableHeaderProps;
    shipViaHeader?: DataTableHeaderProps;
    trackingNumberHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    shipmentDateCell?: DataTableCellProps;
    carrierCell?: DataTableCellProps;
    shipViaCell?: DataTableCellProps;
    trackingNumberCell?: DataTableCellProps;
    // XNG-Changes
    tableHeadStyle?: DataTableHeadProps;
    tableHeader?: DataTableHeaderProps;
    tableRowStyle?: DataTableRowProps;
    tableCellGridContainerStyle?: GridContainerProps;
    trackingNumberCellWrapper?: InjectableCss;
    urlTrackingStyle?: LinkPresentationProps;
    shipmentPackageDataStyles?: ShipmentPackageDataStyles;
    shipmentTrackingUrlsStyles?: ShipmentTrackingUrlsStyles;
}

// <XNG-Changes>
export interface XngShipmentPackageDto extends ShipmentPackageDto {
    ItcStatus: string;
}
// </XNG-Changes>

export const shipmentPackagesStyles: OrderDetailsShipmentPackagesStyles = {};

// <XNG-Changes>
export const constants = {
    docs: "DOCS",
    invoiceNumber: "INVOICE_NUMBER",
    lineNumber: "LINE_NUMBER",
    productNumber: "PRODUCT_NUMBER",
};

export const headerColumns = ["Shipment Date", "Carrier", "Service", "Tracking #", "Status", "Invoice #", ""];
export const numberOfColumn = headerColumns.length;

// </XNG-Changes>

// <XNG-Changes>
export interface ShipmentPackageDataStyles {
    docLink?: LinkPresentationProps;
}

export const shipmentPackageDataStyles: ShipmentPackageDataStyles = {};

export const ShipmentPackageData: React.FC<{
    id: string;
    task: string;
    order: OrderModel;
}> = ({ id, task, order }) => {
    const styles = shipmentPackageDataStyles;

    const shipmentPackageData = shipmentsTrackingData[id];

    if (!shipmentPackageData) {
        return <>{"– –"}</>;
    }

    if (task === constants.docs) {
        const docs = JSON.parse(order?.properties.shipmentsDocumentsData || "{}")[id];

        if (!docs || docs.length === 0) {
            return <>{"– –"}</>;
        }

        return (
            <>
                {docs.map((doc: any, index: any) => (
                    <Link key={`${id}${index}`} target="_blank" href={doc.FilePath} {...styles.docLink}>
                        {doc.FullName}
                    </Link>
                ))}
            </>
        );
    }

    if (task === constants.invoiceNumber) {
        if (!shipmentPackageData.InvoiceNumber) {
            return <>{"– –"}</>;
        }

        if (typeof window === "undefined") {
            return null;
        }

        return (
            <Link href={`//${window.location.host}/api/invoice/PDF/${shipmentPackageData.InvoiceNumber}`}>
                {shipmentPackageData.InvoiceNumber}
            </Link>
        );
    }

    return null;
};
// </XNG-Changes>

// <XNG-Changes>
export interface OrderDetailsShipmentPackagesRowStyles {
    shipmentModal?: ModalPresentationProps;
    modalDataWrapper?: InjectableCss;
    modalData?: InjectableCss;
    resultDataCell?: DataTableCellProps;
}

export const orderDetailsShipmentPackagesRowStyles: OrderDetailsShipmentPackagesRowStyles = {};

export const OrderDetailsShipmentPackagesRow: React.FC<{
    line: any;
}> = ({ line }) => {
    const styles = orderDetailsShipmentPackagesRowStyles;

    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <Link onClick={() => setIsModalOpen(true)} key={line.Number} target="_blank">
                {line.Number}
            </Link>
            <Modal
                {...styles.shipmentModal}
                sizeVariant="medium"
                isOpen={isModalOpen}
                handleClose={() => {
                    setIsModalOpen(false);
                }}
                headline={translate("Shipment Data")}
            >
                <StyledWrapper {...styles.modalDataWrapper}>
                    <StyledWrapper>
                        <Typography {...styles.modalData}>{translate("Itc Status: ")}</Typography>
                        <Typography>{line.ItcStatus}</Typography>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography {...styles.modalData}>{translate("Carrier: ")}</Typography>
                        <Typography>{line.TrackingNumberItcDetails["Carrier"]}</Typography>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography {...styles.modalData}>{translate("Pro: ")}</Typography>
                        <Typography>{line.TrackingNumberItcDetails["Pro"]}</Typography>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography {...styles.modalData}>{translate("BolNum: ")}</Typography>
                        <Typography>{line.TrackingNumberItcDetails["BolNum"]}</Typography>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography {...styles.modalData}>{translate("PoNumber: ")}</Typography>
                        <Typography>{line.TrackingNumberItcDetails["PoNumber"]}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
                <DataTable className="mobile-view">
                    <DataTableHead>
                        <DataTableHeader>{translate("Status Date")}</DataTableHeader>
                        <DataTableHeader>{translate("GeoLocation")}</DataTableHeader>
                        <DataTableHeader>{translate("StatusDescription")}</DataTableHeader>
                        <DataTableHeader>{translate("LocationDescription")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {line.TrackingNumberItcDetails["LineItems"].map((shipmentLineData: any, index: any) => (
                            <DataTableRow key={index}>
                                <DataTableCell {...styles.resultDataCell} data-th={translate("StatusDate")}>
                                    {shipmentLineData.StatusDateDisplay}
                                </DataTableCell>
                                <DataTableCell {...styles.resultDataCell} data-th={translate("GeoLocation")}>
                                    {shipmentLineData.GeoLocation}
                                </DataTableCell>
                                <DataTableCell {...styles.resultDataCell} data-th={translate("StatusDescription")}>
                                    {shipmentLineData.StatusDescription}
                                </DataTableCell>
                                <DataTableCell {...styles.resultDataCell} data-th={translate("LocationDescription")}>
                                    {shipmentLineData.LocationDescription}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </Modal>
        </>
    );
};
// </XNG-Changes>

// <XNG-Changes>
export interface ShipmentTrackingUrlsStyles {
    orderDetailsShipmentPackagesRowStyles?: OrderDetailsShipmentPackagesRowStyles;
    urlTrackingStyle?: LinkPresentationProps;
}

export const shipmentTrackingUrlsStyles: ShipmentTrackingUrlsStyles = {};

export const ShipmentTrackingUrls: React.FC<{
    shipmentPackageId: string;
    order: OrderModel;
}> = ({ shipmentPackageId, order }) => {
    const styles = shipmentTrackingUrlsStyles;
    if (!order.properties.shipmentsTrackingData) {
        return <>{"– –"}</>;
    }

    const shipmentsTrackingData = JSON.parse(order.properties.shipmentsTrackingData);
    const trackingUrls = shipmentsTrackingData[shipmentPackageId] as {
        Number: string;
        Url: string;
        TrackingNumberItcDetails: any;
    }[];

    if (!trackingUrls) {
        return <>{"– –"}</>;
    }

    // Filter out duplicates by `Number`
    const uniqueTrackingUrls = trackingUrls.filter(
        (item, index, self) => index === self.findIndex(t => t.Number === item.Number),
    );

    return (
        <>
            {uniqueTrackingUrls.map(line =>
                line.Url ? (
                    <Link {...styles.urlTrackingStyle} key={line.Number} href={line.Url} target="_blank">
                        {line.Number}
                    </Link>
                ) : line.TrackingNumberItcDetails ? (
                    <OrderDetailsShipmentPackagesRow line={line} key={line.Number} />
                ) : (
                    <Typography key={line.Number}>{line.Number}</Typography>
                ),
            )}
        </>
    );
};
// </XNG-Changes>

// <XNG-Changes>
export const ShipmentTrackingStatus: React.FC<{ shipmentPackageId: string; order: OrderModel }> = ({
    shipmentPackageId,
    order,
}) => {
    if (!order.properties.shipmentsTrackingData) {
        return <>{"– –"}</>;
    }

    const shipmentsTrackingData = JSON.parse(order.properties.shipmentsTrackingData);
    const trackingUrls = shipmentsTrackingData[shipmentPackageId] as { Number: string; ItcStatus: string }[];

    if (!trackingUrls) {
        return <>{"– –"}</>;
    }

    const uniqueTrackingUrls = trackingUrls.filter(
        (item, index, self) => index === self.findIndex(t => t.Number === item.Number),
    );
    const trackingStatuses = uniqueTrackingUrls.map(t => t.ItcStatus);
    const nonEmptyStatuses = trackingStatuses.filter(s => typeof s === "string" && s.length > 0);

    if (nonEmptyStatuses.length === 0) {
        return <>{"– –"}</>;
    }

    return (
        <>
            {nonEmptyStatuses.map(s => (
                <Typography key={s}>{s}</Typography>
            ))}
        </>
    );
};
// </XNG-Changes>

const OrderDetailsShipmentPackages: React.FC<Props> = ({ hideInvoiceInShipments }) => {
    // <XNG-Changes>
    const styles = useMemo(() => mergeToNew(OrderDetailsTableStyle, shipmentPackagesStyles), []);
    const { value: order } = useContext(OrderStateContext);
    const [showAccordionById, setShowAccordionById] = useState({} as { [key: string]: boolean });
    // </XNG-Changes>

    if (!order || !order.shipmentPackages || order.shipmentPackages.length === 0) {
        return null;
    }

    // XNG-Changes: change structure
    return (
        <StyledWrapper {...styles.tableWrapper}>
            <Typography {...styles.infoLabel}>{translate("Shipping Information")}</Typography>
            <DataTable>
                <DataTableHead {...styles.tableHeadStyle}>
                    {headerColumns.map((column, index) => {
                        if (hideInvoiceInShipments && index === 5) return null;
                        return (
                            <DataTableHeader
                                key={`${column}_${index}`}
                                {...styles.tableHeader}
                                className={index === headerColumns.length - 1 ? "hide-print" : ""}
                            >
                                {translate(column)}
                            </DataTableHeader>
                        );
                    })}
                </DataTableHead>
                <DataTableBody {...styles.tableBodyStyle}>
                    {order.shipmentPackages.map(shipmentPackage => {
                        return (
                            <React.Fragment key={shipmentPackage.id}>
                                <DataTableRow
                                    {...styles.tableRowStyle}
                                    key={shipmentPackage.id}
                                    className={showAccordionById[shipmentPackage.id] ? "accordion-color" : ""}
                                >
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[0])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                <LocalizedDateTime
                                                    dateTime={new Date(shipmentPackage.shipmentDate)}
                                                    options={{
                                                        year: "numeric",
                                                        month: "numeric",
                                                        day: "numeric",
                                                        timeZone: "UTC",
                                                    }}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[1])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                {shipmentPackage.carrier || "– –"}
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[2])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                {shipmentPackage.shipVia || "– –"}
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[3])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                <StyledWrapper {...styles.trackingNumberCellWrapper}>
                                                    <ShipmentTrackingUrls
                                                        shipmentPackageId={shipmentPackage.id}
                                                        order={order}
                                                    />
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[4])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                <StyledWrapper {...styles.trackingNumberCellWrapper}>
                                                    <ShipmentTrackingStatus
                                                        shipmentPackageId={shipmentPackage.id}
                                                        order={order}
                                                    />
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>

                                    {!hideInvoiceInShipments && (
                                        <DataTableCell {...styles.tableCellStyle}>
                                            <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                                <Hidden {...styles.hiddenHeaderStyle}>
                                                    <GridItem {...styles.tableCellGridStyle}>
                                                        {translate(headerColumns[5])}
                                                    </GridItem>
                                                </Hidden>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    <ShipmentPackageData
                                                        id={shipmentPackage.id}
                                                        task={constants.invoiceNumber}
                                                        order={order}
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                        </DataTableCell>
                                    )}
                                    <DataTableCell
                                        {...styles.tableCellStyle}
                                        className={`hide-print ${
                                            showAccordionById[shipmentPackage.id] ? "hide-on-mobile" : ""
                                        }`}
                                    >
                                        <GridItem {...styles.showAccordionGridStyle}>
                                            {shipmentPackages[shipmentPackage.id]?.shipmentPackageLineDtos?.length >
                                                0 && (
                                                <Link
                                                    {...styles.shippingDetailsStyle}
                                                    icon={{
                                                        iconProps: {
                                                            src: showAccordionById[shipmentPackage.id]
                                                                ? ChevronUp
                                                                : ChevronDown,
                                                            size: 13,
                                                            color: "primary.main",
                                                            css: css`
                                                                line-height: 0;
                                                                margin-right: 5px;
                                                                vertical-align: middle;
                                                            `,
                                                        },
                                                        position: "right",
                                                    }}
                                                    onClick={() => {
                                                        setShowAccordionById({
                                                            ...showAccordionById,
                                                            [shipmentPackage.id]:
                                                                !showAccordionById[shipmentPackage.id],
                                                        });
                                                    }}
                                                >
                                                    {translate("Shipping Details")}
                                                </Link>
                                            )}
                                        </GridItem>
                                    </DataTableCell>
                                </DataTableRow>
                                {showAccordionById[shipmentPackage.id] && (
                                    <DataTableRow
                                        key={`shipping-package-${shipmentPackage.id}`}
                                        {...styles.accordionRowStyle}
                                    >
                                        <DataTableCell colSpan={numberOfColumn} {...styles.accordionCellStyle}>
                                            <OrderDetailsShipmentInfoDetails
                                                orderLines={orderLines}
                                                orderLineShipments={
                                                    shipmentPackages[shipmentPackage.id]?.shipmentPackageLineDtos
                                                }
                                            />
                                            <Link
                                                {...styles.shippingDetailsStyle}
                                                icon={{
                                                    iconProps: {
                                                        src: showAccordionById[shipmentPackage.id]
                                                            ? ChevronUp
                                                            : ChevronDown,
                                                        size: 13,
                                                        color: "primary.main",
                                                        css: css`
                                                            line-height: 0;
                                                            margin-right: 5px;
                                                            vertical-align: middle;
                                                        `,
                                                    },
                                                    position: "right",
                                                }}
                                                className="hide-on-desktop"
                                                onClick={() => {
                                                    setShowAccordionById({
                                                        ...showAccordionById,
                                                        [shipmentPackage.id]: !showAccordionById[shipmentPackage.id],
                                                    });
                                                }}
                                            >
                                                {translate("Shipping Details")}
                                            </Link>
                                        </DataTableCell>
                                    </DataTableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(OrderDetailsShipmentPackages),
    definition: {
        allowedContexts: ["OrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
